/**
 *
 * PackageAvailability
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cs from 'classnames';
import Masonry from 'react-masonry-css';

import { useSelector, useDispatch } from 'react-redux';
import PackageAvailabilityCard from '../PackageAvailabilityCard';
import PackageAvailabilityError from '../PackageAvailabilityError';
import { toggleModal } from '../../actions';
import PackageContactForm from '../PackageContactForm';
import Modal from 'Components/Modal';
import Icon from 'Components/Icon';
import useBreakpoints from 'Utils/hooks/useBreakpoints';
import * as COLORS from 'Constants/colors';
import { Helmet } from 'react-helmet';
import { packageSteps } from 'Constants';
import { filterKeys } from 'Features/packages/constants';
import { buildPackageUrl } from 'Features/packages/helpers';
//import { returnImage } from 'Utils';
import { ExplorePackagesBanner } from '../../../../Components/ExplorePackagesBanner';

const selector = ({ packages }) => ({
  results: packages.filtered,
  stateWhen: packages.filterData[filterKeys.WHEN],
  showModal: packages.showModal,
  hasResults: packages.results.length > 0,
  activeStep: packages.activeStep
});

export const getPackagePriceAndRangeDateByFilterWhen = (stateWhen, packageItem) => {
  const pckg = packageItem;
  let rangeDate = { from: null,  to: null};
  let price = packageItem.lowerPrice;
  if (stateWhen) {
    const { type, option } = stateWhen;
    if (type && option) {
      switch (type) {
        case 'MONTH': {
          const searchObj = pckg.filterDataToApply.prices.byMonths.find(
            price => price.month === option.id
          );
          rangeDate['from'] = option.from;
          rangeDate['to']  = option.to;

          price = searchObj ? searchObj.price : { amount: 0, currency: 'USD' };
          break;
        }
        case 'SEASON': {
          const searchObj = pckg.filterDataToApply.prices.bySeasons.find(
            price => price.seasonId === option.id
          );
          rangeDate['from'] = option.from;
          rangeDate['to']  = option.to;
          price = searchObj ? searchObj.price : { amount: 0, currency: 'USD' };

          break;
        }
        default:
          break;
      }
    }
  }
  const { amount, currency } = price;
  const { from, to } = rangeDate;


  return { amount, currency, from, to  };

};

const ConnectedPackageAvailability = props => {
  const dispatch = useDispatch();

  const actions = {
    toggleModal: () => dispatch(toggleModal())
  };
  const state = useSelector(selector);

  return <StyledPackageAvailability {...state} {...props} actions={actions} />;
};

const PackageAvailability = ({
  className,
  results,
  actions,
  hasResults,
  showModal,
  activeStep,
  stateWhen
}) => {
  const { mediumDown, largeDown, xlDown } = useBreakpoints();
  const { toggleModal } = actions;

  const returnColumnQuantity = () => {
    if (mediumDown) {
      return 1;
    } else if (largeDown) {
      return 2;
    } else {
      return 3;
    }
  };

  const renderPackageCard = (pckg) => {
    const priceAndRangeDate = getPackagePriceAndRangeDateByFilterWhen(stateWhen, pckg);
    const { amount, currency, from, to } = priceAndRangeDate;
    return (
      <PackageAvailabilityCard
        key={pckg.id}
        image={pckg.imageWebp || pckg.image}
        price={amount}
        currency={currency}
        title={pckg.title}
        icons={pckg.compositeIcons}
        color={pckg.shadowColor ? pckg.shadowColor : COLORS.green}
        duration={pckg.duration}
        travelOn={pckg.travelOn}
        url={buildPackageUrl(pckg.keyword, pckg.id, from, to)}
        lowerLocalPrice={pckg.lowerLocalPrice}
        basePrice={pckg.basePrice}
        rating={pckg.rating}
      />
    );
  };

  const columns = returnColumnQuantity();
  const firstRowPackages = results.slice(0, columns);
  const remainingPackages = results.slice(columns);
  const showBanner = firstRowPackages.length > 0; // Always show banner after first row if there are any packages

  return (
    <div
      className={cs(className, {
        mobile: xlDown,
        'two-lines': activeStep === packageSteps[2].id
      })}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          Promociones en paquetes vacacionales a todos los destinos del mundo.
          Los Mejores paquetes de viaje para vos | Elegí en MeVuelo
        </title>
        <meta
          name="description"
          content="Los Mejores paquetes de viaje para vos. Promociones en paquetes vacacionales a todos los destinos del mundo. Elegí en MeVuelo."
        />
        <meta
          name="keywords"
          content="paquetes de viaje, promociones en paquetes vacacionales"
        />
      </Helmet>
      <Masonry
        breakpointCols={columns}
        className="card-holder"
        columnClassName="card-holder_column">
        {firstRowPackages.map(renderPackageCard)}
      </Masonry>
      {showBanner && (
        <BannerWrapper>
          <ExplorePackagesBanner />
        </BannerWrapper>
      )}
      {showBanner && (
        <Masonry
          breakpointCols={columns}
          className="card-holder"
          columnClassName="card-holder_column">
          {remainingPackages.map(renderPackageCard)}
        </Masonry>
      )}
      {/* {hasResults && (
        <PackageContactForm
          //twoColumns={!mediumDown}
          title={
            <NotFoundContactTitle>
              <span>¿No encontraste tú paquete ideal?</span>
              <Text size="medium" context="important" alignment="center">
                {' '}
                Completa este formulario y te lo armamos a medida:
              </Text>
            </NotFoundContactTitle>
          }
        />
      )} */}

      {results.length === 0 && hasResults && <PackageAvailabilityError />}
      <Modal show={showModal} transparent onClickOverlay={toggleModal}>
        <ModalWrapper>
          <div className="modal-top-bar">
            <Icon
              name="mv-cross"
              color="white"
              size={24}
              className="button-close"
              onClick={toggleModal}
            />
          </div>
          <PackageContactForm twoColumns={!mediumDown} />
        </ModalWrapper>
      </Modal>
    </div>
  );
};

PackageAvailability.propTypes = {
  className: PropTypes.string,
  activeStep: PropTypes.number,
  stateWhen: PropTypes.any,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      rating: PropTypes.shape({
        name: PropTypes.string,
        tagColor: PropTypes.string
      }),
      lowerPrice: PropTypes.shape({
        currency: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired
      }).isRequired,
      shadowColor: PropTypes.string.isRequired,
      icons: PropTypes.arrayOf(
        PropTypes.shape({
          icon: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired
        })
      )
    })
  ).isRequired,
  hasResults: PropTypes.bool,
  showModal: PropTypes.bool,
  actions: PropTypes.shape({
    toggleModal: PropTypes.func.isRequired,
    onItemClick: PropTypes.func
  })
};

const ModalWrapper = styled.div`
  max-width: 1110px;
  width: 100%;
  margin: auto;
  background: transparent;

  .modal-top-bar {
    width: 100%;
    justify-content: flex-end;
    display: flex;
  }
`;

const StyledPackageAvailability = styled(PackageAvailability)`
  position: relative;
  margin: 0 auto;
  padding: 16px;
  max-width: 1100px;

  &.mobile {
    top: 0;
    margin-top: 0;
  }

  .card-holder {
    display: flex;
    margin-left: -24px;
    width: auto;
  }

  .card-holder_column {
    padding-left: 24px; /* gutter size */
    background-clip: padding-box;

    & > div {
      margin-bottom: 24px;
    }
  }

  @media screen and (max-width: 767px) {
    padding: 16px 14px;

    .card-holder {
      margin-left: 0;
    }

    .card-holder_column {
      padding-left: 0;

      & > div {
        margin-bottom: 18px;
      }
    }
  }
`;

export default ConnectedPackageAvailability;
export { StyledPackageAvailability as PackageAvailability };

const BannerWrapper = styled.div`
  width: 100%;
  margin: ${({ theme }) => theme.spacing.four} auto
    ${({ theme }) => theme.spacing.five};
  max-width: 1100px;

  @media screen and (max-width: 767px) {
    margin: ${({ theme }) => theme.spacing.three} auto
      ${({ theme }) => theme.spacing.four};
  }
`;
